'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useMemo, useRef, useState, } from 'react';
import { Badge } from '../../data-display/badge/index.js';
import { Button, IconButton } from '../../actions/button/index.js';
import { EntityPicker } from '../../forms/entity-picker/index.js';
import { Field, FieldError, FieldLabel } from '../../forms/form-field/index.js';
import { Input } from '../../forms/text-field/index.js';
import { Skeleton } from '../../feedback/skeleton/index.js';
import {} from '../../forms/combobox/index.js';
import { cx } from '../../../internal/cx.js';
import { PlusGlyph } from '../../../internal/shell/icons.js';
import { CloseGlyph, DocGlyph, LinkGlyph } from '../../../internal/shell/panel-icons.js';
const DEFAULT_MESSAGES = {
    title: 'Docs',
    linkDoc: 'Link doc',
    addLink: 'Add link',
    newDoc: 'New doc',
    searchDocs: 'Search docs…',
    noDocs: 'No docs match.',
    empty: 'Nothing linked yet. Link a spec, runbook or notes, or add a link to any page.',
    emptyReadOnly: 'Nothing linked yet.',
    loading: 'Loading linked docs…',
    filterBySpace: 'Filter by Space',
    all: 'All',
    noSpace: 'No Space',
    hiddenByFilter: (n) => `${n} more in other Spaces.`,
    showAll: 'Show all',
    unlink: (t) => `Unlink ${t}`,
    unlinkFailed: 'That didn’t unlink. Try again.',
    linkFailed: 'That didn’t link. Try again.',
    urlLabel: 'Link',
    urlInvalid: 'Enter a full web address that starts with https://',
    titleLabel: 'Title',
    titleOptional: 'optional',
    add: 'Add',
    cancel: 'Cancel',
    newTab: 'opens in a new tab',
    retry: 'Try again',
};
const NO_SPACE = '\u0000none';
function isThenable(v) {
    return !!v && typeof v.then === 'function';
}
/** A full http(s) URL, or null. */
export function parseLinkUrl(raw) {
    const s = raw.trim();
    if (!s)
        return null;
    try {
        const u = new URL(s);
        if ((u.protocol !== 'https:' && u.protocol !== 'http:') || !u.hostname)
            return null;
        return u.href;
    }
    catch {
        return null;
    }
}
function hostOf(href) {
    try {
        return new URL(href).hostname.replace(/^www\./, '');
    }
    catch {
        return '';
    }
}
/**
 * The shared panel of docs and links attached to a record (a ticket, a task, a
 * campaign): Rook Dossier docs and external links, with Space filter chips,
 * open, unlink, link a doc (search picker, New doc) and add a link. The
 * product supplies the items and does the linking; the panel never fetches.
 */
export const LinkedDocsPanel = forwardRef(function LinkedDocsPanel({ items, error, onRetry, notice, readOnly = false, docOptions, onSearchDocs, onLinkDoc, linkedDocIds, onCreateDoc, onAddLink, onUnlink, onOpen, spaceFilter = true, filter: filterProp, defaultFilter = '', onFilterChange, headingLevel: Heading = 'h3', messages: messagesProp, className, ...rest }, ref) {
    const messages = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messagesProp }), [messagesProp]);
    const id = useId();
    const titleId = `${id}-title`;
    const [innerFilter, setInnerFilter] = useState(defaultFilter);
    const filterValue = filterProp !== undefined ? filterProp : innerFilter;
    const [busyId, setBusyId] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [adding, setAdding] = useState(false);
    const listRef = useRef(null);
    const headerRef = useRef(null);
    const addButtonRef = useRef(null);
    const pendingFocus = useRef(undefined);
    const all = useMemo(() => items ?? [], [items]);
    const chips = useMemo(() => {
        const m = new Map();
        for (const it of all) {
            const key = it.space?.id ?? NO_SPACE;
            const cur = m.get(key) ?? { key, name: it.space?.name ?? messages.noSpace, n: 0 };
            cur.n += 1;
            m.set(key, cur);
        }
        return [...m.values()].sort((a, b) => a.key === NO_SPACE ? 1 : b.key === NO_SPACE ? -1 : a.name.localeCompare(b.name));
    }, [all, messages.noSpace]);
    const showChips = spaceFilter && chips.length > 1;
    // A filter for a Space that no longer has rows shows everything.
    const activeKey = !showChips || filterValue === ''
        ? ''
        : filterValue === null
            ? NO_SPACE
            : chips.some((c) => c.key === filterValue)
                ? filterValue
                : '';
    const shown = activeKey ? all.filter((it) => (it.space?.id ?? NO_SPACE) === activeKey) : all;
    const hiddenCount = all.length - shown.length;
    const setFilter = (key) => {
        const next = key === NO_SPACE ? null : key;
        if (filterProp === undefined)
            setInnerFilter(next);
        onFilterChange?.(next);
    };
    // After an unlink, focus goes to the next row (or the previous, or the header).
    useEffect(() => {
        if (pendingFocus.current === undefined)
            return;
        const target = pendingFocus.current;
        pendingFocus.current = undefined;
        const row = target
            ? listRef.current?.querySelector(`[data-item-id="${CSS.escape(target)}"] a`)
            : null;
        (row ??
            headerRef.current?.querySelector('button') ??
            headerRef.current)?.focus();
    }, [items]);
    const unlink = (item) => {
        if (!onUnlink || busyId)
            return;
        setActionError(null);
        const index = shown.findIndex((x) => x.id === item.id);
        const neighbour = shown[index + 1] ?? shown[index - 1];
        const done = () => {
            pendingFocus.current = neighbour?.id ?? null;
            setBusyId(null);
        };
        let result;
        try {
            result = onUnlink(item);
        }
        catch {
            setActionError(messages.unlinkFailed);
            return;
        }
        if (!isThenable(result)) {
            done();
            return;
        }
        setBusyId(item.id);
        result.then(done, () => {
            setBusyId(null);
            setActionError(messages.unlinkFailed);
        });
    };
    const linked = useMemo(() => new Set(linkedDocIds ?? all.flatMap((x) => (x.kind === 'doc' && x.docId ? [x.docId] : []))), [linkedDocIds, all]);
    const canLinkDoc = !readOnly && !!onLinkDoc && !!(docOptions || onSearchDocs);
    const search = useMemo(() => onSearchDocs
        ? async (q, ctx) => (await onSearchDocs(q, ctx)).filter((o) => !linked.has(o.value))
        : undefined, [onSearchDocs, linked]);
    const linkDoc = (docId) => {
        if (!docId || !onLinkDoc)
            return;
        setActionError(null);
        try {
            const r = onLinkDoc(docId);
            if (isThenable(r))
                r.then(undefined, () => setActionError(messages.linkFailed));
        }
        catch {
            setActionError(messages.linkFailed);
        }
    };
    const loading = items === null;
    const shownError = error ?? actionError;
    return (_jsxs("section", { ref: ref, ...rest, "aria-labelledby": titleId, "aria-busy": loading || undefined, className: cx('mn-linked-docs-panel', className), "data-read-only": readOnly || undefined, children: [_jsxs("div", { className: "mn-linked-docs-panel__header", ref: headerRef, tabIndex: -1, children: [_jsxs(Heading, { id: titleId, className: "mn-linked-docs-panel__title", children: [messages.title, all.length > 0 ? (_jsx(Badge, { size: "sm", className: "mn-linked-docs-panel__count", children: all.length })) : null] }), !readOnly && !notice ? (_jsxs("div", { className: "mn-linked-docs-panel__actions", children: [canLinkDoc ? (_jsx(EntityPicker, { "aria-label": messages.linkDoc, ...(docOptions
                                    ? { options: docOptions.filter((o) => !linked.has(o.value)) }
                                    : {}), ...(search ? { onSearch: search } : {}), value: null, onValueChange: linkDoc, searchPlaceholder: messages.searchDocs, emptyMessage: messages.noDocs, align: "end", trigger: _jsx(Button, { size: "sm", variant: "secondary", leadingIcon: _jsx(DocGlyph, {}), children: messages.linkDoc }), footer: onCreateDoc
                                    ? ({ close }) => (_jsx("div", { className: "mn-linked-docs-panel__picker-footer", children: _jsx(Button, { size: "sm", variant: "ghost", block: true, leadingIcon: _jsx(PlusGlyph, {}), onClick: () => {
                                                close();
                                                onCreateDoc();
                                            }, children: messages.newDoc }) }))
                                    : undefined })) : null, onAddLink ? (_jsx(Button, { ref: addButtonRef, size: "sm", variant: "ghost", leadingIcon: _jsx(LinkGlyph, {}), "aria-expanded": adding, "aria-controls": adding ? `${id}-add` : undefined, onClick: () => setAdding((a) => !a), children: messages.addLink })) : null] })) : null] }), adding && onAddLink && !readOnly ? (_jsx(AddLinkForm, { id: `${id}-add`, messages: messages, onAdd: onAddLink, onClose: () => {
                    setAdding(false);
                    requestAnimationFrame(() => addButtonRef.current?.focus());
                } })) : null, notice ? (_jsx("div", { className: "mn-linked-docs-panel__notice", children: notice })) : (_jsxs(_Fragment, { children: [showChips ? (_jsxs("div", { className: "mn-linked-docs-panel__chips", role: "group", "aria-label": messages.filterBySpace, children: [_jsxs("button", { type: "button", className: "mn-linked-docs-panel__chip", "aria-pressed": activeKey === '', onClick: () => setFilter(''), children: [messages.all, " ", _jsx("span", { "aria-hidden": true, children: "\u00B7" }), " ", all.length] }), chips.map((c) => (_jsxs("button", { type: "button", className: "mn-linked-docs-panel__chip", "aria-pressed": activeKey === c.key, onClick: () => setFilter(c.key), children: [c.name, " ", _jsx("span", { "aria-hidden": true, children: "\u00B7" }), " ", c.n] }, c.key)))] })) : null, loading ? (_jsxs("div", { className: "mn-linked-docs-panel__loading", role: "status", children: [_jsx("span", { className: "mn-visually-hidden", children: messages.loading }), _jsx(Skeleton, { height: "2.5rem" }), _jsx(Skeleton, { height: "2.5rem" })] })) : all.length === 0 ? (_jsx("p", { className: "mn-linked-docs-panel__empty", children: readOnly ? messages.emptyReadOnly : messages.empty })) : (_jsx("ul", { className: "mn-linked-docs-panel__list", ref: listRef, children: shown.map((it) => {
                            const external = it.external ?? true;
                            const meta = it.meta ?? (it.kind === 'link' ? hostOf(it.href) : undefined);
                            return (_jsxs("li", { className: "mn-linked-docs-panel__row", "data-item-id": it.id, "data-kind": it.kind, "data-busy": busyId === it.id || undefined, children: [_jsx("span", { className: "mn-linked-docs-panel__icon", "aria-hidden": true, children: it.icon ?? (it.kind === 'doc' ? _jsx(DocGlyph, {}) : _jsx(LinkGlyph, {})) }), _jsxs("a", { className: "mn-linked-docs-panel__link", href: it.href, ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {}), onClick: onOpen ? (e) => onOpen(it, e) : undefined, children: [_jsx("span", { className: "mn-linked-docs-panel__name", children: it.title }), meta ? _jsx("span", { className: "mn-linked-docs-panel__meta", children: meta }) : null, external ? (_jsxs("span", { className: "mn-visually-hidden", children: [" (", messages.newTab, ")"] })) : null] }), it.status ? (_jsx(Badge, { size: "sm", className: "mn-linked-docs-panel__status", children: it.status })) : null, !readOnly && onUnlink && it.unlinkable !== false ? (_jsx(IconButton, { size: "sm", className: "mn-linked-docs-panel__unlink", label: messages.unlink(it.title), icon: _jsx(CloseGlyph, {}), loading: busyId === it.id, disabled: busyId !== null && busyId !== it.id, onClick: () => unlink(it) })) : null] }, it.id));
                        }) })), hiddenCount > 0 ? (_jsxs("p", { className: "mn-linked-docs-panel__hidden", children: [messages.hiddenByFilter(hiddenCount), ' ', _jsx("button", { type: "button", className: "mn-linked-docs-panel__show-all", onClick: () => setFilter(''), children: messages.showAll })] })) : null] })), shownError ? (_jsxs("div", { className: "mn-linked-docs-panel__error", role: "alert", children: [_jsx("span", { children: shownError }), error && onRetry ? (_jsx(Button, { size: "xs", variant: "ghost", onClick: onRetry, children: messages.retry })) : null] })) : null] }));
});
function AddLinkForm({ id, messages, onAdd, onClose, }) {
    const [url, setUrl] = useState('');
    const [title, setTitle] = useState('');
    const [invalid, setInvalid] = useState(false);
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState(false);
    const urlRef = useRef(null);
    useEffect(() => urlRef.current?.focus(), []);
    const submit = (e) => {
        e.preventDefault();
        if (busy)
            return;
        const clean = parseLinkUrl(url);
        if (!clean) {
            setInvalid(true);
            urlRef.current?.focus();
            return;
        }
        setFailed(false);
        const link = { url: clean, title: title.trim() || hostOf(clean) };
        let r;
        try {
            r = onAdd(link);
        }
        catch {
            setFailed(true);
            return;
        }
        if (!isThenable(r)) {
            onClose();
            return;
        }
        setBusy(true);
        r.then(() => onClose(), () => {
            setBusy(false);
            setFailed(true);
        });
    };
    return (
    // Escape anywhere in the form cancels it (the form's own keyboard shortcut).
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    _jsxs("form", { id: id, className: "mn-linked-docs-panel__form", noValidate: true, onSubmit: submit, onKeyDown: (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        }, children: [_jsxs(Field, { invalid: invalid, required: true, children: [_jsx(FieldLabel, { children: messages.urlLabel }), _jsx(Input, { ref: urlRef, type: "url", inputMode: "url", autoComplete: "url", placeholder: "https://", value: url, onChange: (e) => {
                            setUrl(e.target.value);
                            if (invalid)
                                setInvalid(false);
                        } }), invalid ? _jsx(FieldError, { children: messages.urlInvalid }) : null] }), _jsxs(Field, { children: [_jsxs(FieldLabel, { children: [messages.titleLabel, ' ', _jsxs("span", { className: "mn-linked-docs-panel__optional", children: ["(", messages.titleOptional, ")"] })] }), _jsx(Input, { value: title, maxLength: 300, onChange: (e) => setTitle(e.target.value) })] }), failed ? (_jsx("p", { className: "mn-linked-docs-panel__form-error", role: "alert", children: messages.linkFailed })) : null, _jsxs("div", { className: "mn-linked-docs-panel__form-actions", children: [_jsx(Button, { type: "submit", size: "sm", variant: "primary", loading: busy, children: messages.add }), _jsx(Button, { size: "sm", variant: "ghost", onClick: onClose, children: messages.cancel })] })] }));
}
//# sourceMappingURL=linked-docs-panel.js.map